import { useEffect, useState } from 'react';
import type { MangaItem } from '@/mocks/manga';

interface Props {
  item: MangaItem | null;
  onClose: () => void;
}

export default function MangaModal({ item, onClose }: Props) {
  const [followed, setFollowed] = useState(false);

  useEffect(() => {
    if (item) {
      setFollowed(false);
      document.body.style.overflow = 'hidden';
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', onKey);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', onKey);
      };
    }
    return undefined;
  }, [item, onClose]);

  if (!item) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-background-950/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-background-50 md:flex-row">
        <div className="relative w-full shrink-0 overflow-hidden bg-background-100 md:w-64">
          <img src={item.cover} alt={`${item.title} 详情封面`} className="h-52 w-full object-cover md:h-full" />
          <button
            type="button"
            aria-label="关闭"
            onClick={onClose}
            className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-background-950/60 text-background-50 hover:bg-background-950/80"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 md:p-6">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-heading text-2xl text-primary-600">{item.title}</h3>
            {item.exclusive && (
              <span className="rounded-md bg-primary-500 px-2 py-0.5 text-xs font-bold text-background-50">独家</span>
            )}
            {item.preview && (
              <span className="rounded-md bg-accent-500 px-2 py-0.5 text-xs font-bold text-background-50">抢先看</span>
            )}
          </div>
          <p className="mt-1 text-sm text-foreground-600">{item.altTitle} · {item.category}</p>

          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { icon: 'ri-book-2-line', label: '话数', value: `${item.episodes} 话` },
              { icon: 'ri-eye-line', label: '人气', value: item.views },
              { icon: 'ri-star-fill', label: '评分', value: item.rating },
              { icon: 'ri-time-line', label: '更新', value: item.updateTime },
            ].map((m) => (
              <div key={m.label} className="rounded-xl border border-background-200 bg-background-100 p-3 text-center">
                <i className={`${m.icon} text-base text-accent-500`}></i>
                <p className="mt-1 text-sm font-bold text-foreground-950">{m.value}</p>
                <p className="text-xs text-foreground-600">{m.label}</p>
              </div>
            ))}
          </div>

          <p className="mt-4 text-sm leading-relaxed text-foreground-700">{item.desc}</p>

          <div className="mt-4 flex flex-wrap gap-2">
            <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">高清原版</span>
            {item.uncensored && <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">未删减</span>}
            <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">{item.status}</span>
            <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">每日同步</span>
          </div>

          <div className="mt-5">
            <h4 className="mb-2 text-sm font-bold text-foreground-950">最近更新</h4>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {[`${item.title} ${item.latest}`, `${item.title} 番外特辑`, `${item.title} 精选壁纸`].map((c) => (
                <div key={c} className="truncate rounded-lg border border-background-200 bg-background-50 px-3 py-2 text-xs text-foreground-700">
                  {c}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={() => setFollowed((v) => !v)}
              className={`inline-flex h-11 flex-1 items-center justify-center whitespace-nowrap rounded-full text-sm font-semibold transition-colors ${
                followed
                  ? 'bg-accent-100 text-accent-800'
                  : 'bg-primary-500 text-background-50 hover:bg-primary-600'
              }`}
            >
              <i className={`${followed ? 'ri-check-line' : 'ri-heart-add-line'} mr-2 text-base`}></i>
              {followed ? '已加入追更' : '加入追更'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-11 flex-1 items-center justify-center whitespace-nowrap rounded-full border border-background-300 bg-background-50 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-book-open-line mr-2 text-base"></i>
              返回列表
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}